// The initiative's cited sources ("meeting 14c6e0b3 2026-08-27") as things a
// reader can open (kanban 9eb5f005). Rows written before contract v8 carry
// citations but no quotes; until the next run quotes them, this is the link
// out from the question to the source that the 9/28 check-in asked for.

import type { SourceRef } from '../../services/roadmapReports';
import { sourceRefFromCitation } from './evidenceUtils';
import type { EvidenceSource } from './roadmapTimelineUtils';
import { evidenceKind, evidenceLabel } from './roadmapTimelineUtils';
import type { OpenSourceFn } from './EvidenceList';
import styles from './RoadmapV2.module.css';

interface Props {
  sources: readonly string[];
  onOpenSource?: OpenSourceFn;
  /** The source ledger, so a cited meeting reads as its name. */
  ledger?: readonly EvidenceSource[];
  /** A sentence before the list (the questions card says why there is no quote). */
  lead?: string | null;
  /** Render as the pop-up's plain list (no lead, no muted frame). */
  plain?: boolean;
  /** Say what each citation is ("meeting", "email", "knowledge bank") in front
   * of it, instead of grouping the list under a heading that read like a tab
   * (owner, review vhztut: "what is this 'meeting' tab?"). */
  withKind?: boolean;
}

export function CitedSources({ sources, onOpenSource, ledger = [], lead = null, plain = false, withKind = false }: Props) {
  if (sources.length === 0) {
    return lead ? <div className={`${styles.muted} ${styles.evidenceEmpty}`} data-testid="evidence-empty">{lead} No sources cited.</div> : null;
  }
  const items = sources.map((s) => {
    const ref: SourceRef | null = sourceRefFromCitation(s);
    const label = evidenceLabel(s, ledger);
    return (
      <li key={s} className={label === s && !ref ? styles.mono : undefined} title={label === s ? undefined : s}>
        {withKind && <span className={styles.evidenceKind}>{evidenceKind(s)}</span>}
        {ref && onOpenSource ? (
          // A renamed source keeps the raw citation on hover, as the plain text did.
          <button type="button" className={styles.linkBtn} onClick={() => onOpenSource(ref)} title={label === s ? 'Open this source' : s}>{label}</button>
        ) : (
          label
        )}
      </li>
    );
  });
  if (plain) return <ul className={styles.plainList}>{items}</ul>;
  return (
    <div className={`${styles.muted} ${styles.evidenceEmpty}`} data-testid="evidence-empty">
      {lead && <div>{lead}</div>}
      <ul className={styles.evidenceCited} aria-label="Cited sources">{items}</ul>
    </div>
  );
}
